import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { 
  Menu, X, Heart, Users, Phone, Home as HomeIcon, 
  Info, HandHeart, MessageCircle, ChevronDown, ChevronUp,
  Facebook, Mail, MapPin, ExternalLink,
  Shirt, HeartHandshake, BookOpen,
  Baby, GraduationCap, Building, FileText, Puzzle, Sparkles,
  Shield
} from "lucide-react";

// Assets from NestCares website - optimized for web
const ASSETS = {
  heroBg: "https://static.wixstatic.com/media/41d000_9bace29101c0110bf92b86118c5802c4.png/v1/fill/w_800,h_417,al_t,q_80,enc_avif,quality_auto/41d000_9bace29101c0110bf92b86118c5802c4.png",
  logoSquare: "https://static.wixstatic.com/media/b1de0f_da331072d32f42aaa7764f89abb5701f~mv2.jpg/v1/fill/w_153,h_158,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Logo%20-%20FINAL.jpg",
  logoFull: "https://static.wixstatic.com/media/b1de0f_e27cf5f5543c4c40b6c51ceaeefaa5f9~mv2.png/v1/fill/w_160,h_195,al_c,q_80,enc_avif,quality_auto/LOGO%20-%20Website.png",
  aboutBanner: "https://static.wixstatic.com/media/b1de0f_58e7af260f14454490cec96f2bae724d~mv2.jpg/v1/fill/w_640,h_301,al_c,q_80,enc_avif,quality_auto/About%20Us_edited.jpg",
  nicole: "https://static.wixstatic.com/media/b1de0f_1729ef2c26f847368f7e9898a8d895b4~mv2.jpg/v1/fill/w_150,h_150,al_c,q_80,enc_avif,quality_auto/Nicole%20About%20Me_edited.jpg",
  candace: "https://static.wixstatic.com/media/b1de0f_00a3646c36144f18b481a84677d6b607~mv2.jpg/v1/fill/w_150,h_150,al_c,q_80,enc_avif,quality_auto/Candace%20About%20Me.jpg",
  kristen: "https://static.wixstatic.com/media/b1de0f_a7c2ef0e4f2a4b49adb53bb81f5046ad~mv2.jpg/v1/fill/w_150,h_150,al_c,q_80,enc_avif,quality_auto/1000031699_edited.jpg",
  // Optimized images - smaller sizes
  sensoryTools: "https://customer-assets.emergentagent.com/job_nest-modern/artifacts/wr7ucnqj_6730941405979407236.jpg",
  childWithHeadphones: "https://customer-assets.emergentagent.com/job_nest-modern/artifacts/yftu98fz_982033451024905043.jpg",
};

// Gallery images for Photo Gallery section - User photos first
const GALLERY_IMAGES = [
  // User's new photos from Facebook
  { src: "/gallery/571149342_122142994274912663_1351546961031435193_n.jpg", alt: "NEST Project community event" },
  { src: "/gallery/647520576_122166837440912663_3868263240469120080_n.jpg", alt: "Family receiving support from NEST Project" },
  { src: "/gallery/648782689_122166837398912663_5987949621255835173_n.jpg", alt: "NEST Project helping families" },
  { src: "/gallery/646329379_122166837122912663_5851976755165455554_n.jpg", alt: "Community support event" },
  { src: "/gallery/574479761_122144801594912663_4542711698443095726_n.jpg", alt: "NEST Project volunteers" },
  { src: "/gallery/568344423_122141594156912663_6118631637326216838_n.jpg", alt: "Children with special needs resources" },
  // Original images
  { src: "https://customer-assets.emergentagent.com/job_nest-modern/artifacts/wr7ucnqj_6730941405979407236.jpg", alt: "Sensory tools including textured fidget bracelets and Alpine Muffy Kids hearing protection" },
  { src: "https://customer-assets.emergentagent.com/job_nest-modern/artifacts/yftu98fz_982033451024905043.jpg", alt: "Happy child wearing noise-canceling headphones giving a thumbs up" },
];

const LINKS = {
  jotformSupport: "https://form.jotform.com/252636778466069",
  jotformVolunteer: "https://form.jotform.com/252445114816050",
  facebook: "https://www.facebook.com/share/1Bzu8je1zo/",
  donate: "https://givebutter.com/NESTproject",
  candidProfile: "https://app.candid.org/profile/16403293/the-nest-project/?pkId=sensory-safe-kids&isActive=true",
};

const MARQUEE_WORDS = ["Hope", "Strength", "Support", "Community", "Compassion", "Healing", "Belonging", "Encouragement", "Resilience", "Care"];

// Animation variants - optimized for performance
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.4, ease: "easeOut" } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.4, ease: "easeOut" } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// Animated Word Component - simplified for performance
function AnimatedWord({ word, delay = 0, color }) {
  return (
    <motion.span
      className="inline-block mx-4 md:mx-8"
      style={{ color }}
      initial={{ opacity: 0.3 }}
      animate={{ opacity: [0.3, 0.8, 0.3] }}
      transition={{
        duration: 2,
        delay,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    >
      {word}
    </motion.span>
  );
}

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

// Navigation Component - ABOVE LOGO DESIGN, SPREAD OUT
function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/programs-resources", label: "Programs" },
    { to: "/get-support", label: "Get Support" },
    { to: "/community", label: "Community" },
    { to: "/contact", label: "Contact" },
  ];

  const mobileNavLinks = [
    { to: "/", label: "Home", icon: HomeIcon },
    { to: "/about", label: "About Us", icon: Info },
    { to: "/990-forms", label: "990 Forms", icon: FileText },
    { to: "/programs-resources", label: "Programs & Resources", icon: HandHeart },
    { to: "/get-support", label: "Get Support", icon: Heart },
    { to: "/community", label: "Community", icon: Users },
    { to: "/contact", label: "Contact", icon: Phone },
  ];

  return (
    <>
      <a href="#main-content" className="skip-link" data-testid="skip-link">
        Skip to main content
      </a>
      
      {/* Desktop Nav - ABOVE LOGO, spread out centered */}
      <motion.nav 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-4 left-0 right-0 mx-auto z-50 hidden lg:flex items-center justify-center gap-2 bg-white/95 backdrop-blur-md rounded-full px-6 py-3 shadow-lg border border-[#FFDA5E]/30 max-w-4xl ${scrolled ? 'shadow-xl' : ''}`}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Home link */}
        <Link
          to="/"
          className={`nav-link whitespace-nowrap text-sm font-medium px-3 py-2 rounded-full transition-all ${location.pathname === '/' ? 'bg-[#FFDA5E] text-[#1A1A1A]' : 'text-[#1A1A1A] hover:bg-[#FFDA5E]/50'}`}
          data-testid="nav-home"
        >
          Home
        </Link>

        {/* About Dropdown */}
        <div 
          className="relative"
          onMouseEnter={() => setAboutDropdownOpen(true)}
          onMouseLeave={() => setAboutDropdownOpen(false)}
        >
          <button
            className={`nav-link whitespace-nowrap text-sm font-medium px-3 py-2 rounded-full transition-all flex items-center gap-1 ${(location.pathname === '/about' || location.pathname === '/990-forms') ? 'bg-[#FFDA5E] text-[#1A1A1A]' : 'text-[#1A1A1A] hover:bg-[#FFDA5E]/50'}`}
            data-testid="nav-about-dropdown"
          >
            About
            <ChevronDown size={14} className={`transition-transform ${aboutDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          
          <AnimatePresence>
            {aboutDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden min-w-[160px]"
              >
                <Link
                  to="/about"
                  className={`block px-4 py-3 text-sm font-medium transition-colors ${location.pathname === '/about' ? 'bg-[#FFDA5E] text-[#1A1A1A]' : 'text-[#1A1A1A] hover:bg-[#FFDA5E]/30'}`}
                  data-testid="nav-about-us"
                >
                  About Us
                </Link>
                <Link
                  to="/990-forms"
                  className={`block px-4 py-3 text-sm font-medium transition-colors border-t border-gray-100 ${location.pathname === '/990-forms' ? 'bg-[#FFDA5E] text-[#1A1A1A]' : 'text-[#1A1A1A] hover:bg-[#FFDA5E]/30'}`}
                  data-testid="nav-990-forms"
                >
                  990 Forms
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Other nav links */}
        {navLinks.slice(1).map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className={`nav-link whitespace-nowrap text-sm font-medium px-3 py-2 rounded-full transition-all ${location.pathname === link.to ? 'bg-[#FFDA5E] text-[#1A1A1A]' : 'text-[#1A1A1A] hover:bg-[#FFDA5E]/50'}`}
            data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {link.label}
          </Link>
        ))}
      </motion.nav>

      {/* Mobile Nav */}
      <div className="lg:hidden fixed top-4 left-4 right-4 z-50 flex justify-between items-center">
        <Link to="/" data-testid="mobile-logo">
          <motion.img 
            src={ASSETS.logoFull} 
            alt="Nest Cares Logo" 
            className="h-14 w-auto shadow-lg"
            width="57"
            height="70"
            whileHover={{ scale: 1.1 }}
            loading="lazy" 
          />
        </Link>
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          className="p-3 rounded-full glass shadow-lg"
          aria-expanded={isOpen}
          aria-label={isOpen ? "Close menu" : "Open menu"}
          data-testid="mobile-menu-toggle"
          whileTap={{ scale: 0.9 }}
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </motion.button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mobile-nav lg:hidden"
          >
            {mobileNavLinks.map((link, index) => (
              <motion.div
                key={link.to}
                initial={{ opacity: 0, y: 20, x: -20 }}
                animate={{ opacity: 1, y: 0, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={link.to}
                  onClick={() => setIsOpen(false)}
                  className={`mobile-nav-link flex items-center gap-3 ${location.pathname === link.to ? 'bg-[#FFDA5E]' : ''}`}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <link.icon size={24} />
                  {link.label}
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// Floating Volunteer CTA - positioned on left
function FloatingVolunteerCTA() {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.a
      href={LINKS.jotformSupport}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 left-6 z-40 flex items-center gap-3 bg-gradient-to-r from-[#FF9800] to-[#E53935] text-white px-5 py-4 rounded-full shadow-xl cursor-pointer"
      initial={{ x: -100, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ delay: 1, duration: 0.5 }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid="floating-volunteer-cta"
    >
      <motion.div
        animate={{ rotate: isHovered ? 360 : 0 }}
        transition={{ duration: 0.5 }}
      >
        <Sparkles size={24} />
      </motion.div>
      <div className="flex flex-col">
        <span className="text-xs font-medium opacity-90">Need Help?</span>
        <span className="font-bold text-sm">Request Assistance</span>
      </div>
      <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#FFDA5E] rounded-full" />
    </motion.a>
  );
}

// Candid Transparency Seal - positioned on right
// Single Animated Marquee Banner for Homepage
function HomeMarqueeBanner() {
  const colors = ['#FFDA5E', '#E53935', '#FF9800', '#4CAF50', '#03A9F4', '#00BCD4', '#9C27B0', '#FFDA5E', '#E53935', '#FF9800'];
  
  return (
    <div className="py-8 overflow-hidden bg-gradient-to-r from-[#FAFAF9] via-white to-[#FAFAF9]" aria-hidden="true">
      <Marquee speed={50} pauseOnHover gradient={false}>
        {MARQUEE_WORDS.map((word, i) => (
          <span
            key={i}
            className="mx-8 md:mx-12 text-4xl md:text-6xl font-bold opacity-80"
            style={{ 
              fontFamily: 'Fredoka, sans-serif',
              color: colors[i % colors.length],
            }}
          >
            {word}
          </span>
        ))}
      </Marquee>
    </div>
  );
}

// Social Links Component - Footer version with visible icons
function SocialLinksFooter() {
  return (
    <div className="flex gap-4 items-center">
      <motion.a
        href={LINKS.facebook}
        target="_blank"
        rel="noopener noreferrer"
        className="w-14 h-14 rounded-full bg-[#1877F2] flex items-center justify-center text-white shadow-lg"
        aria-label="Follow us on Facebook"
        data-testid="footer-social-facebook"
        whileHover={{ scale: 1.15, rotate: -5 }}
        whileTap={{ scale: 0.95 }}
      >
        <Facebook size={26} />
      </motion.a>
      <motion.a
        href={LINKS.donate}
        target="_blank"
        rel="noopener noreferrer"
        className="w-14 h-14 rounded-full bg-gradient-to-br from-[#E53935] to-[#FF9800] flex items-center justify-center text-white shadow-lg"
        aria-label="Donate to The NEST Project"
        data-testid="footer-social-donate"
        whileHover={{ scale: 1.15, rotate: 5 }}
        whileTap={{ scale: 0.95 }}
      >
        <Heart size={26} />
      </motion.a>
    </div>
  );
}

// Social Links Component
function SocialLinks({ size = "normal" }) {
  const sizeClass = size === "large" ? "w-16 h-16" : "w-14 h-14";
  const iconSize = size === "large" ? 28 : 24;
  
  return (
    <div className="flex gap-4 items-center">
      <motion.a
        href={LINKS.facebook}
        target="_blank"
        rel="noopener noreferrer"
        className={`social-btn facebook ${sizeClass}`}
        aria-label="Follow us on Facebook"
        data-testid="social-facebook"
        whileHover={{ scale: 1.15, rotate: -5 }}
        whileTap={{ scale: 0.95 }}
      >
        <Facebook size={iconSize} />
      </motion.a>
    </div>
  );
}

// Photo Gallery Component
function PhotoGallery() {
  return (
    <section className="section-spacing bg-[#FAFAF9]" aria-labelledby="gallery-heading">
      <div className="container-nest">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-12"
        >
          <motion.span variants={fadeInUp} className="accent-font text-2xl text-[#03A9F4]">
            Our Community
          </motion.span>
          <motion.h2 
            id="gallery-heading"
            variants={fadeInUp} 
            className="text-3xl md:text-5xl font-bold mt-2"
          >
            Photo Gallery
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-[#4A4A4A] mt-4 max-w-2xl mx-auto">
            See the impact we're making in our community and the families we support.
          </motion.p>
        </motion.div>
        
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6"
        >
          {GALLERY_IMAGES.map((image, i) => (
            <motion.div
              key={i}
              variants={fadeInUp}
              className={`relative overflow-hidden rounded-2xl shadow-lg ${i === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className={`aspect-square ${i === 0 ? 'md:aspect-[4/3]' : ''}`}>
                <img 
                  src={image.src} 
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  loading="lazy"
                />
              </div>
              <motion.div 
                className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end p-4"
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
              >
                <p className="text-white text-sm font-medium">{image.alt}</p>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-10"
        >
          <motion.a
            href={LINKS.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-[#1877F2] text-white px-6 py-3 rounded-full font-semibold shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-testid="gallery-facebook-link"
          >
            <Facebook size={20} />
            See More on Facebook
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
}

// Accordion Component
function Accordion({ title, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="accordion-item">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="accordion-trigger"
        aria-expanded={isOpen}
        data-testid={`accordion-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <span>{title}</span>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
          <ChevronDown size={20} />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="accordion-content">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Footer Component
function Footer() {
  return (
    <footer className="footer" role="contentinfo">
      <div className="container-nest">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Logo & Mission */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <img src={ASSETS.logoFull} alt="Nest Cares" className="h-24 w-auto mb-6" width="78" height="96" loading="lazy" />
            <p className="text-white/80 mb-6">
              Nurturing and equipping children with disabilities by providing essential items and support services.
            </p>
            <SocialLinksFooter />
          </motion.div>
          
          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <h3 className="text-lg font-semibold mb-4 text-[#FFDA5E]">Quick Links</h3>
            <nav aria-label="Footer navigation">
              <ul className="space-y-3">
                <li><Link to="/" data-testid="footer-home">Home</Link></li>
                <li><Link to="/about" data-testid="footer-about">About Us</Link></li>
                <li><Link to="/programs-resources" data-testid="footer-programs">Programs & Resources</Link></li>
                <li><Link to="/get-support" data-testid="footer-support">Get Support</Link></li>
                <li><Link to="/community" data-testid="footer-community">Community</Link></li>
                <li><Link to="/contact" data-testid="footer-contact">Contact</Link></li>
              </ul>
            </nav>
          </motion.div>
          
          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="text-lg font-semibold mb-4 text-[#FFDA5E]">Contact Us</h3>
            <address className="not-italic space-y-3 text-white/80">
              <p className="flex items-start gap-3">
                <MapPin size={20} className="flex-shrink-0 mt-1" />
                <span>1319 W State St<br />New Castle, PA 16101</span>
              </p>
              <p className="flex items-center gap-3">
                <Mail size={20} />
                <a href="mailto:thenestproject.us@gmail.com" data-testid="footer-email">
                  thenestproject.us@gmail.com
                </a>
              </p>
            </address>
          </motion.div>
          
          {/* Get Involved */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="text-lg font-semibold mb-4 text-[#FFDA5E]">Get Involved</h3>
            <p className="text-white/80 mb-4">
              Join us in making a difference for children with disabilities and their families.
            </p>
            <div className="flex flex-col gap-3">
              <motion.a
                href={LINKS.donate}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-gradient-to-r from-[#E53935] to-[#FF9800] text-white px-6 py-3 rounded-full font-semibold text-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                data-testid="footer-donate-btn"
              >
                <Heart className="inline mr-2" size={18} />
                Donate Now
              </motion.a>
              <motion.a
                href={LINKS.jotformVolunteer}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-[#FFDA5E] text-[#1A1A1A] px-6 py-3 rounded-full font-semibold text-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                data-testid="footer-volunteer-btn"
              >
                Volunteer Now
              </motion.a>
            </div>
          </motion.div>
        </div>
        
        {/* Partnership Section */}
        <div className="border-t border-white/20 pt-8 pb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h3 className="text-lg font-semibold mb-3 text-[#FFDA5E]">Partner With Us</h3>
            <p className="text-white/80 text-sm">
              We're actively seeking partnerships and sponsorships with businesses and organizations who share our commitment to making a meaningful difference in the lives of children with special needs and their families. Together, we can create lasting impact in our community.
            </p>
            <motion.a
              href="mailto:thenestproject.us@gmail.com?subject=Partnership%20Inquiry"
              className="inline-block mt-4 text-[#FFDA5E] font-semibold hover:underline"
              whileHover={{ scale: 1.05 }}
            >
              Contact us about partnerships →
            </motion.a>
          </motion.div>
        </div>
        
        {/* Bottom bar */}
        <div className="border-t border-white/20 pt-8 text-center">
          <p className="accent-font text-2xl text-[#FFDA5E] mb-4">
            Supporting families. Strengthening communities.
          </p>
          <p className="text-white/60 text-sm mb-4">
            &copy; {new Date().getFullYear()} The NEST Project. All rights reserved.
          </p>
          <p className="text-white/50 text-xs max-w-2xl mx-auto mb-6">
            The NEST Project is a nonprofit organization recognized as tax-exempt under Section 501(c)(3) of the Internal Revenue Code. All contributions are tax-deductible to the fullest extent permitted by law. Our EIN is 39-2865756.
          </p>
          
          {/* Candid Transparency Seal */}
          <motion.a
            href={LINKS.candidProfile}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-testid="candid-seal"
            title="View our profile on Candid"
          >
            <img
              src="/platinum-transparency-2026.jpg"
              alt="Candid Platinum Transparency 2026 Seal"
              className="w-24 h-24 rounded-lg shadow-lg mx-auto"
              width="96"
              height="96"
              loading="lazy"
            />
          </motion.a>
        </div>
      </div>
    </footer>
  );
}

// HOME PAGE
function HomePage() {
  const testimonials = [
    {
      text: "The NEST Project helped put food on the table for my two boys. Times are tough with the government shut down. I am not working due to both my boys having ADHD and Autism diagnosis. Between therapies and doctor appts, it's difficult.",
    },
    {
      text: "At a time where prices keep going up, we are so grateful for the assistance. Offering healthy options can be even more costly. The gift card is so nice and allows us to choose options that our family eats. Thank you!",
    },
    {
      text: "This couldn't have come at a better time. I have been out of work caring for my child for almost 2 years. Money is tight and I didn't know how I was going to put food on the table. Receiving your card and gift card was a huge relief at a very stressful time in my life.",
    },
  ];

  return (
    <main id="main-content">
      {/* Hero Section */}
      <section className="hero-section" aria-labelledby="hero-heading">
        <div className="hero-bg">
          <div className="hero-overlay"></div>
        </div>
        
        <div className="container-nest relative z-10 pt-32 lg:pt-40">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-4xl mx-auto text-center"
          >
            <motion.div 
              variants={scaleIn} 
              className="mb-8"
            >
              <img 
                src={ASSETS.logoFull} 
                alt="Nest Cares Logo - A yellow chick holding a heart in a colorful nest" 
                className="mx-auto w-40 h-auto"
                width={160}
                height={195}
                fetchPriority="high"
              />
            </motion.div>
            
            <motion.h1 
              id="hero-heading"
              variants={fadeInUp} 
              className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-[#1A1A1A]"
            >
              Helping Children with Special Needs{' '}
              <span className="text-gradient inline-block">THRIVE</span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-xl md:text-2xl text-[#4A4A4A] mb-10 max-w-2xl mx-auto"
            >
              Nest Cares connects families with resources, education, and support that help children and parents thrive.
            </motion.p>
            
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <motion.a 
                href={LINKS.donate}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                data-testid="hero-donate"
                whileHover={{ scale: 1.05, boxShadow: "0 10px 30px rgba(255, 218, 94, 0.5)" }}
                whileTap={{ scale: 0.95 }}
              >
                <Heart className="inline mr-2" size={20} />
                Donate
              </motion.a>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link 
                  to="/programs-resources"
                  className="btn-secondary"
                  data-testid="hero-explore-programs"
                >
                  Explore Programs & Resources
                </Link>
              </motion.div>
            </motion.div>
            
            {/* Prominent Social Links */}
            <motion.div 
              variants={fadeInUp}
              className="mt-12 flex flex-col items-center"
            >
              <p className="text-[#4A4A4A] mb-4 font-medium">Follow Our Journey</p>
              <SocialLinks size="large" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Single Animated Marquee Banner */}
      <HomeMarqueeBanner />

      {/* Impact / What We Provide Section */}
      <section className="section-spacing" aria-labelledby="impact-heading">
        <div className="container-nest">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.span variants={fadeInUp} className="accent-font text-2xl text-[#4CAF50]">
                Making a Difference
              </motion.span>
              <motion.h2 
                id="impact-heading"
                variants={fadeInUp} 
                className="text-3xl md:text-5xl font-bold mb-6 mt-2"
              >
                What We Provide
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-[#4A4A4A] mb-6">
                The NEST Project provides essential, practical support to children with disabilities and their families, meeting everyday needs that are often overlooked or unaffordable.
              </motion.p>
              <motion.ul variants={staggerContainer} className="space-y-4">
                {[
                  "Sensory-friendly tools like weighted vests and noise-canceling headphones",
                  "Adaptive equipment and accessibility resources for children with disabilities",
                  "Safety supports for children and their families",
                  "Home safety equipment for children at risk of elopement",
                ].map((item, i) => (
                  <motion.li 
                    key={i} 
                    variants={fadeInLeft}
                    className="flex items-start gap-3"
                  >
                    <motion.div
                      whileHover={{ scale: 1.2, rotate: 10 }}
                      className="flex-shrink-0 mt-1"
                    >
                      <Heart size={20} className="text-[#E53935]" fill="#E53935" />
                    </motion.div>
                    <span className="text-[#4A4A4A]">{item}</span>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <motion.div 
                className="rounded-[2rem] overflow-hidden shadow-2xl"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <img 
                  src={ASSETS.sensoryTools} 
                  alt="Sensory tools including textured fidget bracelets and Alpine Muffy Kids hearing protection headphones" 
                  className="w-full h-auto"
                  width={637}
                  height={676}
                  loading="lazy"
                />
              </motion.div>
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-[#FFDA5E] rounded-full opacity-60" />
              <div className="absolute -top-6 -right-6 w-16 h-16 bg-[#03A9F4] rounded-full opacity-60" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-spacing bg-[#FAFAF9]" aria-labelledby="stats-heading">
        <div className="container-nest">
          <h2 id="stats-heading" className="sr-only">Our Impact</h2>
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-3 gap-8"
          >
            {[
              { number: "100%", label: "Volunteer-Run" },
              { number: "PA & OH", label: "Serving Both States" },
              { number: "24/7", label: "Support Available" },
            ].map((stat, i) => (
              <motion.div 
                key={i} 
                variants={scaleIn} 
                className="stat-item"
              >
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Happy Child Section */}
      <section className="section-spacing" aria-labelledby="success-heading">
        <div className="container-nest">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative order-2 lg:order-1"
            >
              <motion.div 
                className="rounded-[2rem] overflow-hidden shadow-2xl"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <img 
                  src={ASSETS.childWithHeadphones} 
                  alt="Happy child wearing noise-canceling headphones giving a thumbs up" 
                  className="w-full h-auto"
                  width={637}
                  height={849}
                  loading="lazy"
                />
              </motion.div>
              <div className="absolute -bottom-6 -right-6 w-20 h-20 bg-[#4CAF50] rounded-full opacity-60" />
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-[#E53935] rounded-full opacity-60" />
            </motion.div>
            
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="order-1 lg:order-2"
            >
              <motion.span variants={fadeInUp} className="accent-font text-2xl text-[#E53935]">
                Real Impact
              </motion.span>
              <motion.h2 
                id="success-heading"
                variants={fadeInUp} 
                className="text-3xl md:text-5xl font-bold mb-6 mt-2"
              >
                Helping Children Thrive
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-[#4A4A4A] mb-6">
                Through The NEST Project, we strive to provide comfort and essentials so no child goes without the tools or items they need to feel safe and supported.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-lg text-[#4A4A4A] mb-6">
                Whether it's offering relief in a tough moment or helping a child feel more confident and comfortable in their day-to-day life, NEST Essentials and Support exists to lift the load so families can focus on what matters most — watching their children grow, heal, and thrive.
              </motion.p>
              <motion.div variants={fadeInUp}>
                <motion.div whileHover={{ x: 10 }} className="inline-block">
                  <Link 
                    to="/programs-resources"
                    className="inline-flex items-center gap-2 text-[#E53935] font-semibold"
                    data-testid="learn-more-link"
                  >
                    Learn About Our Programs
                    <ChevronDown className="rotate-[-90deg]" size={20} />
                  </Link>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="section-spacing bg-[#FAFAF9]" aria-labelledby="about-preview-heading">
        <div className="container-nest">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.span variants={fadeInUp} className="accent-font text-2xl text-[#FF9800]">
                Our Story
              </motion.span>
              <motion.h2 
                id="about-preview-heading"
                variants={fadeInUp} 
                className="text-3xl md:text-5xl font-bold mb-6 mt-2"
              >
                Founded by Parents Who Understand
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-[#4A4A4A] mb-6">
                We're Nicole and Cody, parents to two amazing kiddos, each with unique needs. Our son is on the autism spectrum, and our daughter lives with IBD and juvenile arthritis.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-lg text-[#4A4A4A] mb-8">
                That's why we created The NEST Project — to nurture and equip children with disabilities by providing essential items and support services that ease the burdens families face.
              </motion.p>
              <motion.div variants={fadeInUp}>
                <motion.div whileHover={{ x: 10 }} className="inline-block">
                  <Link 
                    to="/about"
                    className="inline-flex items-center gap-2 text-[#E53935] font-semibold"
                    data-testid="about-read-more"
                  >
                    Read Our Full Story
                    <ChevronDown className="rotate-[-90deg]" size={20} />
                  </Link>
                </motion.div>
              </motion.div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <motion.div 
                className="rounded-[2rem] overflow-hidden shadow-2xl"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <img 
                  src={ASSETS.aboutBanner} 
                  alt="Nicole and Cody with their family" 
                  className="w-full h-auto"
                  width={906}
                  height={426}
                  loading="lazy"
                />
              </motion.div>
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-[#FFDA5E] rounded-full opacity-60" />
              <div className="absolute -top-6 -right-6 w-16 h-16 bg-[#03A9F4] rounded-full opacity-60" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Programs Preview */}
      <section className="section-spacing" aria-labelledby="programs-heading">
        <div className="container-nest">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="accent-font text-2xl text-[#4CAF50]">
              What We Offer
            </motion.span>
            <motion.h2 
              id="programs-heading"
              variants={fadeInUp} 
              className="text-3xl md:text-5xl font-bold mt-2"
            >
              Programs & Resources
            </motion.h2>
          </motion.div>
          
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {[
              { icon: Puzzle, title: "Sensory Tools", desc: "Weighted vests, fidget items, noise-canceling headphones, and more.", color: "blue" },
              { icon: HandHeart, title: "Adaptive Equipment", desc: "Accessibility resources and adaptive equipment for children with disabilities.", color: "yellow" },
              { icon: Shield, title: "Safety Supports", desc: "Tools and resources that help keep children safe at home and on the go.", color: "red" },
              { icon: HeartHandshake, title: "Daily Necessities", desc: "Comfort items that support a child's routine and emotional well-being.", color: "purple" },
              { icon: BookOpen, title: "Resource Directory", desc: "Comprehensive guides to local and statewide support services.", color: "orange" },
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className={`nest-card ${item.color} card-lift`}
                whileHover={{ y: -10, boxShadow: "0 25px 50px rgba(0,0,0,0.15)" }}
              >
                <motion.div 
                  className={`icon-circle ${item.color}`}
                  whileHover={{ scale: 1.1, rotate: 10 }}
                >
                  <item.icon size={28} />
                </motion.div>
                <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-[#4A4A4A]">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link 
                to="/programs-resources"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="view-all-programs"
              >
                View All Programs & Resources
                <ChevronDown className="rotate-[-90deg]" size={20} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-spacing bg-[#FAFAF9]" aria-labelledby="testimonials-heading">
        <div className="container-nest">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="accent-font text-2xl text-[#E53935]">
              Family Stories
            </motion.span>
            <motion.h2 
              id="testimonials-heading"
              variants={fadeInUp} 
              className="text-3xl md:text-5xl font-bold mt-2"
            >
              Hear From Our Community
            </motion.h2>
          </motion.div>
          
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {testimonials.map((testimonial, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className="testimonial-card card-lift"
              >
                <div className="text-[#FFDA5E] mb-4">
                  <Heart size={32} fill="#FFDA5E" />
                </div>
                <blockquote className="testimonial-quote">
                  "{testimonial.text}"
                </blockquote>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Photo Gallery */}
      <PhotoGallery />

      {/* CTA Section */}
      <section className="cta-section" aria-labelledby="cta-heading">
        <div className="container-nest">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="max-w-3xl mx-auto"
          >
            <motion.h2 
              id="cta-heading"
              variants={fadeInUp} 
              className="text-3xl md:text-5xl font-bold mb-6 text-[#1A1A1A]"
            >
              Ready to Make a Difference?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-[#1A1A1A]/80 mb-10">
              Whether you need support or want to help others, we're here for you.
            </motion.p>
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap"
            >
              <motion.a 
                href={LINKS.donate}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-[#E53935] to-[#FF9800] text-white px-8 py-4 rounded-full font-bold text-lg shadow-lg inline-flex items-center gap-2"
                data-testid="cta-donate"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Heart size={20} />
                Donate Now
              </motion.a>
              <motion.a 
                href={LINKS.jotformSupport}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#1A1A1A] text-white px-8 py-4 rounded-full font-bold text-lg shadow-lg"
                data-testid="cta-get-support"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Request Support
              </motion.a>
              <motion.a 
                href={LINKS.jotformVolunteer}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-[#1A1A1A] px-8 py-4 rounded-full font-bold text-lg shadow-lg"
                data-testid="cta-volunteer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Become a Volunteer
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Meet the Team Preview */}
      <section className="section-spacing" aria-labelledby="team-heading">
        <div className="container-nest">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="accent-font text-2xl text-[#9C27B0]">
              The People Behind NEST
            </motion.span>
            <motion.h2 
              id="team-heading"
              variants={fadeInUp} 
              className="text-3xl md:text-5xl font-bold mt-2"
            >
              Meet Our Team
            </motion.h2>
          </motion.div>
          
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              { img: ASSETS.nicole, name: "Nicole Carbone", role: "Founder & President" },
              { img: ASSETS.candace, name: "Candace Higbee", role: "Secretary" },
              { img: ASSETS.kristen, name: "Kristen Brommer", role: "Treasurer" },
            ].map((member, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className="nest-card staff-card card-lift"
                whileHover={{ y: -10 }}
              >
                <motion.img 
                  src={member.img} 
                  alt={member.name}
                  className="staff-image"
                  loading="lazy"
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: "spring", stiffness: 300 }}
                />
                <h3 className="text-xl font-bold">{member.name}</h3>
                <p className="text-[#4A4A4A]">{member.role}</p>
              </motion.div>
            ))}
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <motion.div whileHover={{ x: 10 }} className="inline-block">
              <Link 
                to="/about"
                className="inline-flex items-center gap-2 text-[#E53935] font-semibold"
                data-testid="meet-team-link"
              >
                Learn More About Our Team
                <ChevronDown className="rotate-[-90deg]" size={20} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final Social CTA */}
      <section className="py-16 bg-[#1A1A1A] text-white text-center">
        <div className="container-nest">
          <motion.h2 
            className="text-2xl md:text-4xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Connect With Us & Support Our Mission
          </motion.h2>
          <motion.p 
            className="text-white/80 mb-8 max-w-xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Stay updated on our programs and see the impact we're making together.
          </motion.p>
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <motion.a
              href={LINKS.donate}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-[#E53935] to-[#FF9800] text-white px-8 py-4 rounded-full font-bold text-lg inline-flex items-center gap-2 shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Heart size={20} />
              Donate Now
            </motion.a>
            <motion.a
              href={LINKS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#1877F2] text-white px-8 py-4 rounded-full font-bold text-lg inline-flex items-center gap-2 shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Facebook size={20} />
              Follow Us on Facebook
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Floating Volunteer CTA */}
      <FloatingVolunteerCTA />
    </main>
  );
}

// ABOUT PAGE
function AboutPage() {
  return (
    <main id="main-content">
      <div className="page-header">
        <div className="container-nest">
          <motion.span 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="accent-font text-2xl text-[#FF9800]"
          >
            Get to Know Us
          </motion.span>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-bold mt-2"
          >
            About The NEST Project
          </motion.h1>
        </div>
      </div>

      {/* Mission - NOW FIRST */}
      <section className="section-spacing bg-[#FAFAF9]" aria-labelledby="mission-heading">
        <div className="container-nest">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.h2 
              id="mission-heading"
              variants={fadeInUp} 
              className="text-3xl md:text-4xl font-bold mb-6"
            >
              Our Mission
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-[#4A4A4A]">
              We are dedicated to nurturing and equipping children with disabilities by providing essential items, inclusive resources, and support for families, especially during times of crisis.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-xl text-[#4A4A4A] mt-6">
              In addition to helping families navigate everyday needs, we also recognize that many children in Pennsylvania face crisis situations — living in shelters, entering emergency care, or losing their belongings. Through The NEST Project, we strive to provide comfort and essentials during these times, so no child goes without the tools or items they need to feel safe and supported.
            </motion.p>
            <motion.p variants={fadeInUp} className="accent-font text-3xl text-[#E53935] mt-8">
              Together, we can nurture, equip, support, and help children with special needs thrive.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Our Story - NOW SECOND */}
      <section className="section-spacing" aria-labelledby="story-heading">
        <div className="container-nest">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <motion.div 
                className="rounded-[2rem] overflow-hidden shadow-2xl"
                whileHover={{ scale: 1.02 }}
              >
                <img 
                  src={ASSETS.aboutBanner} 
                  alt="Nicole and Cody, founders of The NEST Project" 
                  className="w-full h-auto"
                />
              </motion.div>
            </motion.div>
            
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.h2 
                id="story-heading"
                variants={fadeInUp} 
                className="text-3xl md:text-4xl font-bold mb-6"
              >
                Our Story
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-[#4A4A4A] mb-6">
                We're Nicole and Cody, parents to two incredible children who each have their own unique strengths and complexities. Like many families, our journey into the world of special needs and disability support wasn't something we planned for, but it has become one of the most meaningful parts of our lives.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-lg text-[#4A4A4A] mb-6">
                Over the years, we've learned firsthand how challenging it can be for families to find the resources, support, and understanding they need. Between therapies, specialists, school supports, and everyday life, the emotional and financial demands can quickly become overwhelming.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-lg text-[#4A4A4A] mb-6">
                We also discovered that many of the items children rely on, from adaptive equipment and sensory tools to everyday comfort and support items, are often difficult to access. Families frequently wait months, and sometimes even years, for insurance approvals for equipment their children truly need. Unfortunately, many helpful items aren't covered at all, leaving families to shoulder the cost on their own. This creates a significant gap between what children need and what families are actually able to access.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-lg text-[#4A4A4A] mb-6">
                We knew we couldn't be the only ones facing these challenges. That realization is what led us to create <strong>The NEST Project</strong>.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-lg text-[#4A4A4A] font-semibold italic">
                Every child deserves the opportunity to thrive, feel included, and be supported by a community that understands their unique journey.
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section-spacing bg-[#FAFAF9]" aria-labelledby="team-section-heading">
        <div className="container-nest">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="accent-font text-2xl text-[#9C27B0]">
              The Hearts Behind The Mission
            </motion.span>
            <motion.h2 
              id="team-section-heading"
              variants={fadeInUp} 
              className="text-3xl md:text-5xl font-bold mt-2"
            >
              Meet Our Staff
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-[#4A4A4A] max-w-2xl mx-auto mt-4">
              Behind The NEST Project is a small but passionate team of people who understand the journey, because we're living it, too.
            </motion.p>
          </motion.div>
          
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              { 
                img: ASSETS.nicole, 
                name: "Nicole Carbone", 
                role: "Founder & President",
                bio: "I'm a devoted mom of two children with disabilities and the heart behind The NEST Project. With lived experience navigating therapies, specialists, and the everyday challenges of raising children with unique needs, I am passionate about supporting other families and making sure no child goes without the essentials that help them thrive."
              },
              { 
                img: ASSETS.candace, 
                name: "Candace Higbee", 
                role: "Secretary",
                bio: "I'm a registered nurse and a mom of three, including my two-year-old son who has autism. Since his diagnosis, I've experienced firsthand the challenges and uncertainties that come with this journey. Being part of this nonprofit allows me to advocate for other families and help provide tools, resources, and support they need."
              },
              { 
                img: ASSETS.kristen, 
                name: "Kristen Brommer", 
                role: "Treasurer",
                bio: "With over six years of experience in the nonprofit sector, I bring a unique blend of financial expertise, data management, and heartfelt advocacy to my work. In every role I take on, I lead with empathy, precision, and a steadfast belief that every child, and every family, deserves to feel seen, supported, and empowered."
              },
            ].map((member, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className="nest-card card-lift text-center"
                whileHover={{ y: -10 }}
              >
                <motion.img 
                  src={member.img} 
                  alt={member.name}
                  className="staff-image"
                  loading="lazy"
                  whileHover={{ scale: 1.1 }}
                />
                <h3 className="text-xl font-bold">{member.name}</h3>
                <p className="text-[#E53935] font-medium mb-4">{member.role}</p>
                <p className="text-[#4A4A4A] text-sm">{member.bio}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Donate CTA */}
      <section className="py-16 bg-gradient-to-r from-[#E53935] to-[#FF9800] text-center">
        <div className="container-nest">
          <motion.h2 
            className="text-2xl md:text-4xl font-bold mb-6 text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Support Our Mission
          </motion.h2>
          <motion.p 
            className="text-white/90 mb-8 max-w-xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Your donation helps us provide essential items and support to children with special needs and their families.
          </motion.p>
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <motion.a
              href={LINKS.donate}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-[#E53935] px-8 py-4 rounded-full font-bold text-lg inline-flex items-center gap-2 shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Heart size={20} />
              Donate Now
            </motion.a>
            <motion.a
              href={LINKS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#1877F2] text-white px-8 py-4 rounded-full font-bold text-lg inline-flex items-center gap-2 shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Facebook size={20} />
              Follow Us
            </motion.a>
          </motion.div>
        </div>
      </section>
    </main>
  );
}

// PROGRAMS & RESOURCES PAGE
function ProgramsResourcesPage() {
  const paResources = [
    { name: "Bureau of Special Education", url: "https://www.pa.gov/agencies/education/programs-and-services/instruction/elementary-and-secondary-education/special-education#.Va5ynNjbK70" },
    { name: "Office of Child Development and Early Learning", url: "https://www.pa.gov/agencies/dhs" },
    { name: "Early Intervention – Birth to Three", url: "https://www.pa.gov/agencies/dhs/resources/early-learning-child-care/early-intervention-services" },
    { name: "Early Intervention – Three to School Age", url: "https://www.pa.gov/agencies/education/programs-and-services/instruction/early-learning/early-intervention" },
    { name: "Office of Developmental Programs (ODP)", url: "https://www.pa.gov/agencies/dhs/departments-offices/odp-info" },
  ];

  return (
    <main id="main-content">
      <div className="page-header">
        <div className="container-nest">
          <motion.span 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="accent-font text-2xl text-[#4CAF50]"
          >
            Support & Guidance
          </motion.span>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-bold mt-2"
          >
            Programs & Resources
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-[#4A4A4A] mt-4 max-w-2xl mx-auto"
          >
            "Sometimes the smallest tools make the biggest difference in a child's world."
          </motion.p>
        </div>
      </div>

      {/* NEST Essentials Program */}
      <section className="section-spacing" aria-labelledby="essentials-heading">
        <div className="container-nest">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="max-w-4xl mx-auto"
          >
            <motion.h2 
              id="essentials-heading"
              variants={fadeInUp} 
              className="text-3xl md:text-4xl font-bold mb-6 text-center"
            >
              NEST Essentials and Support
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-[#4A4A4A] mb-8 text-center">
              The NEST Essentials and Support program is at the heart of The NEST Project's mission. This program provides essential, practical support to children with disabilities and their families, meeting everyday needs that are often overlooked or unaffordable.
            </motion.p>
            
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 gap-6 mb-12"
            >
              {[
                { icon: Puzzle, title: "Sensory-Friendly Tools", desc: "Weighted vests, fidget items, noise-canceling headphones" },
                { icon: HandHeart, title: "Adaptive Equipment & Accessibility", desc: "Adaptive equipment and accessibility resources for children with disabilities" },
                { icon: Shield, title: "Safety Supports", desc: "Tools and resources that help keep children safe at home and on the go" },
                { icon: Heart, title: "Daily Necessities", desc: "Comfort items that support routine and emotional well-being" },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  variants={fadeInUp}
                  className="flex gap-4 items-start p-6 bg-white rounded-2xl shadow-sm border-l-4 border-[#4CAF50]"
                  whileHover={{ x: 10, boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}
                >
                  <motion.div 
                    className="icon-circle green flex-shrink-0"
                    whileHover={{ scale: 1.1, rotate: 10 }}
                  >
                    <item.icon size={24} />
                  </motion.div>
                  <div>
                    <h3 className="font-bold mb-1">{item.title}</h3>
                    <p className="text-[#4A4A4A] text-sm">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
            
            <motion.div variants={fadeInUp} className="text-center">
              <motion.a
                href={LINKS.jotformSupport}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="apply-support-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Heart size={20} />
                Apply for Assistance
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* The NEST Closet - Coming Soon */}
      <section className="section-spacing bg-[#FAFAF9]" aria-labelledby="nest-closet-heading">
        <div className="container-nest">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="max-w-4xl mx-auto"
          >
            <motion.div 
              variants={fadeInUp}
              className="relative bg-gradient-to-r from-[#9C27B0] to-[#E53935] p-8 md:p-12 rounded-[2rem] shadow-xl text-white overflow-hidden"
            >
              <div className="absolute top-4 right-4 bg-[#FFDA5E] text-[#1A1A1A] px-4 py-2 rounded-full font-bold text-sm">
                NEW PROGRAM
              </div>
              
              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center">
                    <Shield size={48} className="text-white" />
                  </div>
                </div>
                
                <div className="text-center md:text-left">
                  <h2 id="nest-closet-heading" className="text-2xl md:text-3xl font-bold mb-4">
                    Home Safety Program
                  </h2>
                  <p className="text-white/90 text-lg">
                    Helping families with children at risk of elopement with home safety modifications such as cameras, sensors, safety gates, locks, and more!
                  </p>
                  <p className="text-white/80 mt-4 text-sm">
                    Stay tuned for updates on our Facebook page!
                  </p>
                  <motion.a
                    href={LINKS.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-4 bg-white text-[#9C27B0] px-6 py-3 rounded-full font-semibold"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Facebook size={20} />
                    Follow for Updates
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Autism Resources */}
      <section className="section-spacing" aria-labelledby="autism-heading">
        <div className="container-nest">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="max-w-4xl mx-auto"
          >
            <motion.h2 
              id="autism-heading"
              variants={fadeInUp} 
              className="text-3xl md:text-4xl font-bold mb-6 text-center"
            >
              Concerned About Autism? Know the Signs
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-[#4A4A4A] mb-8 text-center">
              Early identification of autism spectrum disorder (ASD) can make a significant difference in a child's development.
            </motion.p>
            
            <div className="space-y-4">
              <Accordion title="Early Signs (12–24 Months)" defaultOpen>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Limited or no eye contact</li>
                  <li>Lack of babbling, gestures (like pointing or waving), or first words</li>
                  <li>Doesn't respond to their name</li>
                  <li>Limited interest in people or play</li>
                  <li>Repetitive behaviors (hand-flapping, rocking, spinning objects)</li>
                  <li>Strong attachment to routines or resistance to change</li>
                </ul>
              </Accordion>
              
              <Accordion title="Preschool and Early Childhood (Ages 2–5)">
                <ul className="list-disc pl-6 space-y-2">
                  <li>Delayed speech or language skills</li>
                  <li>Difficulty understanding social cues or emotions</li>
                  <li>Prefers playing alone or doesn't engage in pretend play</li>
                  <li>Unusual sensitivity to sound, texture, or light</li>
                  <li>Fixation on specific topics or toys</li>
                </ul>
              </Accordion>
              
              <Accordion title="School-Age and Beyond">
                <ul className="list-disc pl-6 space-y-2">
                  <li>Difficulty making or keeping friends</li>
                  <li>Trouble with back-and-forth conversation</li>
                  <li>Rigid thinking or inflexible routines</li>
                  <li>Repetitive language or echolalia (repeating phrases)</li>
                </ul>
              </Accordion>
            </div>
            
            <motion.p variants={fadeInUp} className="text-[#4A4A4A] mt-8 text-center text-sm">
              This information was developed by the <a href="https://paautism.org/" target="_blank" rel="noopener noreferrer" className="text-[#03A9F4] hover:underline">Autism Services, Education, Resources, and Training Collaborative (ASERT)</a>. For more information, contact ASERT at 877-231-4244 or <a href="mailto:info@PAautism.org" className="text-[#03A9F4] hover:underline">info@PAautism.org</a>.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* PA State Resources */}
      <section className="section-spacing" aria-labelledby="pa-resources-heading">
        <div className="container-nest">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="max-w-4xl mx-auto"
          >
            <motion.h2 
              id="pa-resources-heading"
              variants={fadeInUp} 
              className="text-3xl md:text-4xl font-bold mb-6 text-center"
            >
              Pennsylvania State Offices
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-[#4A4A4A] mb-8 text-center">
              Connect with state resources for children with disabilities and their families.
            </motion.p>
            
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 gap-4"
            >
              {paResources.map((resource, i) => (
                <motion.a
                  key={i}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variants={fadeInUp}
                  className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm border-l-4 border-[#03A9F4]"
                  data-testid={`pa-resource-${i}`}
                  whileHover={{ x: 10, boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}
                >
                  <Building size={24} className="text-[#03A9F4] flex-shrink-0" />
                  <span className="font-medium">{resource.name}</span>
                  <ExternalLink size={16} className="ml-auto text-[#4A4A4A]" />
                </motion.a>
              ))}
            </motion.div>
            
            <motion.div variants={fadeInUp} className="mt-8 text-center">
              <motion.a
                href="https://www.pa.gov/agencies/dhs/resources/intellectual-disabilities-autism/autism-services"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#E53935] font-semibold"
                data-testid="odp-link"
                whileHover={{ x: 10 }}
              >
                Bureau of Supports for Autism and Special Populations
                <ExternalLink size={16} />
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-[#FAFAF9]">
        <div className="container-nest">
          <p className="text-sm text-[#71717A] text-center max-w-4xl mx-auto">
            <strong>Disclaimer:</strong> The NEST Project provides informational resources intended to assist families and individuals; however, this information should not be considered medical, legal, or professional advice. Referrals to external organizations are offered as suggestions and do not constitute endorsements or formal recommendations.
          </p>
        </div>
      </section>
    </main>
  );
}

// GET SUPPORT PAGE
function GetSupportPage() {
  return (
    <main id="main-content">
      <div className="page-header">
        <div className="container-nest">
          <motion.span 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="accent-font text-2xl text-[#E53935]"
          >
            We're Here For You
          </motion.span>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-bold mt-2"
          >
            Get Support
          </motion.h1>
        </div>
      </div>

      <section className="section-spacing">
        <div className="container-nest">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12"
            >
              <motion.img 
                src={ASSETS.logoFull} 
                alt="Nest Cares" 
                className="mx-auto w-32 h-auto mb-8"
              />
              <h2 className="text-2xl md:text-3xl font-bold mb-6">
                If You're In Need of Assistance, We're Here to Help
              </h2>
              <p className="text-lg text-[#4A4A4A] mb-8">
                The NEST Project provides essential support to children with disabilities and their families. Whether you need sensory-friendly tools, adaptive equipment, safety supports, or holiday food assistance, please fill out our application form below.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-r from-[#FFDA5E] to-[#FF9800] p-12 rounded-[2rem] shadow-xl"
            >
              <div>
                <Heart size={64} className="mx-auto mb-6 text-[#1A1A1A]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-[#1A1A1A]">Apply for Assistance</h3>
              <p className="text-[#1A1A1A]/80 mb-8">
                Complete our application form to request support for your family.
              </p>
              <motion.a
                href={LINKS.jotformSupport}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#1A1A1A] text-white px-10 py-5 rounded-full font-bold text-xl inline-flex items-center gap-3 shadow-lg"
                data-testid="apply-now-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FileText size={24} />
                Apply Now
              </motion.a>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-12 p-8 bg-[#FAFAF9] rounded-2xl"
            >
              <h3 className="text-xl font-bold mb-4">What We Can Help With:</h3>
              <div className="grid md:grid-cols-2 gap-4 text-left">
                {[
                  "Sensory-friendly tools (weighted vests, noise-canceling headphones)",
                  "Adaptive equipment and accessibility resources",
                  "Safety supports for children and families",
                  "Daily necessities and comfort items",
                ].map((item, i) => (
                  <motion.div 
                    key={i} 
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                  >
                    <Heart size={16} className="text-[#E53935]" fill="#E53935" />
                    <span>{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}

// COMMUNITY PAGE
function CommunityPage() {
  return (
    <main id="main-content">
      <div className="page-header">
        <div className="container-nest">
          <motion.span 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="accent-font text-2xl text-[#FF9800]"
          >
            Join Our Mission
          </motion.span>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-bold mt-2"
          >
            Community
          </motion.h1>
        </div>
      </div>

      {/* Volunteer Section */}
      <section className="section-spacing" aria-labelledby="volunteer-heading">
        <div className="container-nest">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.h2 
                id="volunteer-heading"
                variants={fadeInUp} 
                className="text-3xl md:text-4xl font-bold mb-6"
              >
                Calling All Volunteers!
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-[#4A4A4A] mb-6">
                We're looking for passionate volunteers who want to make a real difference for children with disabilities and their families.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-lg text-[#4A4A4A] mb-6">
                Whether you have experience in advocacy, social media, donor outreach, event assistance, community networking, or simply a heart for helping, we would love to have you on our team.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-lg text-[#4A4A4A] mb-8">
                Every helping hand matters, and we'd love to have you on the team!
              </motion.p>
              <motion.div variants={fadeInUp}>
                <motion.a
                  href={LINKS.jotformVolunteer}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="volunteer-apply-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Users size={20} />
                  Apply to Volunteer
                </motion.a>
              </motion.div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <motion.div 
                className="bg-gradient-to-r from-[#FFDA5E] to-[#FF9800] p-12 rounded-[2rem] shadow-xl"
                whileHover={{ scale: 1.02 }}
              >
                <h3 className="text-2xl font-bold mb-6 text-[#1A1A1A]">Ways You Can Help:</h3>
                <ul className="space-y-4 text-[#1A1A1A]">
                  {[
                    "Advocacy and awareness",
                    "Social media support",
                    "Donor outreach",
                    "Event assistance",
                    "Community networking",
                    "Delivering donations to families",
                  ].map((item, i) => (
                    <motion.li 
                      key={i} 
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <Heart size={20} fill="#1A1A1A" />
                      <span className="font-medium">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
              <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-[#4CAF50] rounded-full opacity-60" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Connect */}
      <section className="section-spacing bg-[#FAFAF9]" aria-labelledby="social-heading">
        <div className="container-nest text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              id="social-heading"
              variants={fadeInUp} 
              className="text-3xl md:text-4xl font-bold mb-6"
            >
              Connect With Our Community
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-[#4A4A4A] mb-8 max-w-2xl mx-auto">
              Follow us on social media to stay updated, share your story, and connect with other families.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <motion.a
                href={LINKS.donate}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-[#E53935] to-[#FF9800] text-white px-8 py-4 rounded-full font-bold text-lg inline-flex items-center gap-2 justify-center shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Heart size={20} />
                Donate Now
              </motion.a>
              <motion.a
                href={LINKS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#1877F2] text-white px-8 py-4 rounded-full font-bold text-lg inline-flex items-center gap-2 justify-center shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Facebook size={20} />
                Follow Us on Facebook
              </motion.a>
            </motion.div>
            <motion.p variants={fadeInUp} className="accent-font text-2xl text-[#E53935]">
              Share your story with us!
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Photo Gallery on Community Page */}
      <PhotoGallery />
    </main>
  );
}


// 990 FORMS PAGE - Simple display of 990 forms
function Forms990Page() {
  const [expandedYears, setExpandedYears] = useState({ "2025": true });

  // Static 990 forms data
  const formsData = {
    "2025": {
      title: "Form 990-N (e-Postcard)",
      description: "Electronic Notice for Tax-Exempt Organizations not Required to File Form 990 or 990-EZ",
      image: "https://customer-assets.emergentagent.com/job_charity-profile-link/artifacts/qk21bwjj_4991076959028657614.jpg"
    }
  };

  const years = Object.keys(formsData).sort((a, b) => b - a);

  const toggleYear = (year) => {
    setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }));
  };

  return (
    <main id="main-content">
      <div className="page-header">
        <div className="container-nest">
          <motion.span 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="accent-font text-2xl text-[#4CAF50]"
          >
            Financial Transparency
          </motion.span>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-bold mt-2"
          >
            View Our 990 Forms
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-[#4A4A4A] mt-4 max-w-2xl mx-auto"
          >
            As a 501(c)(3) nonprofit organization, we are committed to transparency. View our annual tax filings below.
          </motion.p>
        </div>
      </div>

      <section className="section-spacing">
        <div className="container-nest max-w-4xl">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="space-y-4"
          >
            {years.map((year) => (
              <motion.div
                key={year}
                variants={fadeInUp}
                className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100"
              >
                <button
                  onClick={() => toggleYear(year)}
                  className="w-full px-6 py-5 flex items-center justify-between bg-gradient-to-r from-[#FAFAF9] to-white hover:from-[#f0f0f0] transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#4CAF50] flex items-center justify-center">
                      <FileText size={24} className="text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-2xl font-bold text-[#1A1A1A]">{year}</h3>
                      <p className="text-[#4A4A4A] text-sm">{formsData[year].title}</p>
                    </div>
                  </div>
                  <motion.div
                    animate={{ rotate: expandedYears[year] ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown size={24} className="text-[#4A4A4A]" />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {expandedYears[year] && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 py-6 border-t border-gray-100">
                        <p className="text-[#4A4A4A] mb-4">{formsData[year].description}</p>
                        <motion.div 
                          className="rounded-xl overflow-hidden shadow-md border border-gray-200"
                          whileHover={{ scale: 1.01 }}
                        >
                          <img 
                            src={formsData[year].image} 
                            alt={`${year} ${formsData[year].title}`}
                            className="w-full h-auto"
                          />
                        </motion.div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>

          {/* Candid Seal Link */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <a
              href={LINKS.candidProfile}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 text-[#4A4A4A] hover:text-[#4CAF50] transition-colors"
            >
              <Shield size={24} />
              <span>View our full profile on Candid</span>
              <ExternalLink size={16} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Floating elements */}
      <FloatingVolunteerCTA />
    </main>
  );
}


// CONTACT PAGE
function ContactPage() {
  return (
    <main id="main-content">
      <div className="page-header">
        <div className="container-nest">
          <motion.span 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="accent-font text-2xl text-[#03A9F4]"
          >
            Get In Touch
          </motion.span>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-bold mt-2"
          >
            Contact Us
          </motion.h1>
        </div>
      </div>

      <section className="section-spacing">
        <div className="container-nest">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp} className="mb-12">
                <motion.img 
                  src={ASSETS.logoFull} 
                  alt="Nest Cares" 
                  className="w-40 h-auto mb-8"
                  whileHover={{ scale: 1.05 }}
                />
                <h2 className="text-2xl font-bold mb-4">The NEST Project</h2>
                <p className="text-lg text-[#4A4A4A] mb-6">
                  We'd love to hear from you! Whether you have questions, need support, or want to get involved, reach out and we'll get back to you.
                </p>
              </motion.div>
              
              <motion.div variants={fadeInUp} className="space-y-6">
                <motion.div 
                  className="flex items-start gap-4"
                  whileHover={{ x: 10 }}
                >
                  <div className="icon-circle blue">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Mailing Address</h3>
                    <address className="not-italic text-[#4A4A4A]">
                      1319 W State St<br />
                      New Castle, PA 16101
                    </address>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="flex items-start gap-4"
                  whileHover={{ x: 10 }}
                >
                  <div className="icon-circle yellow">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Email</h3>
                    <a 
                      href="mailto:thenestproject.us@gmail.com"
                      className="text-[#03A9F4] hover:underline"
                      data-testid="contact-email"
                    >
                      thenestproject.us@gmail.com
                    </a>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="flex items-start gap-4"
                  whileHover={{ x: 10 }}
                >
                  <div className="icon-circle green">
                    <Heart size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Follow Us</h3>
                    <div className="mt-2">
                      <SocialLinks />
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
            
            {/* Volunteer CTA */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <motion.div 
                className="bg-gradient-to-r from-[#FFDA5E] to-[#FF9800] p-12 rounded-[2rem] shadow-xl h-full flex flex-col justify-center"
                whileHover={{ scale: 1.02 }}
              >
                <div>
                  <Users size={64} className="mb-6 text-[#1A1A1A]" />
                </div>
                <h2 className="text-3xl font-bold mb-4 text-[#1A1A1A]">Want to Volunteer?</h2>
                <p className="text-[#1A1A1A]/80 mb-8 text-lg">
                  We're always looking for caring, passionate people to join us in making a difference! Whether you're interested in helping behind the scenes, assisting at outreach events, or delivering donations to families in need, there's a place for you here.
                </p>
                <motion.a
                  href={LINKS.jotformVolunteer}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#1A1A1A] text-white px-8 py-4 rounded-full font-bold text-lg inline-flex items-center gap-3 shadow-lg self-start"
                  data-testid="contact-volunteer-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Users size={20} />
                  Click to Apply
                </motion.a>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}

// Main App Component
function App() {
  return (
    <div className="App min-h-screen">
      <BrowserRouter>
        <ScrollToTop />
        <Navigation />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/programs-resources" element={<ProgramsResourcesPage />} />
          <Route path="/get-support" element={<GetSupportPage />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/990-forms" element={<Forms990Page />} />
          <Route path="/contact" element={<ContactPage />} />
        </Routes>
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;
